import { Howl } from "howler";
import { useRef, useState } from "react";
import { fetchUrlMetadata } from "../fetch-bytescale-metadata";
import { getCanPlayAAC } from "../helpers/get-can-play-aac";
import { processAudioFilePath } from "../process-file-path";

export type PlayErrorType = "load" | "play" | "fetch";

interface IPlayOptions {
  onError?: (error: string, type: PlayErrorType) => void;
  maxDuration?: number;
}

export interface IAudioPlayer {
  pause: () => void;
  play: (source: string, options?: IPlayOptions) => void;
  playing: boolean;
  playedSource: string | null;
  loading: boolean;
}

export function useAudioPlayer(accountId?: string): IAudioPlayer {
  const [currentAudio, setCurrentAudio] = useState<Howl | null>(null);
  const [playing, setPlaying] = useState(false);
  const [playedSource, setPlayedSource] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const requestCounter = useRef(0);

  const pause = () => {
    if (currentAudio) {
      currentAudio.pause();
      setPlaying(false);
    }
  };

  const play = async (source: string, { onError }: IPlayOptions = {}) => {
    if (!accountId) {
      return;
    }

    if (currentAudio) {
      currentAudio.stop();
    }
    setLoading(true);
    setPlayedSource(source);
    const canPlayAAC = await getCanPlayAAC();
    const src = processAudioFilePath(source, accountId, {
      transformationParams: {
        f: canPlayAAC ? "aac" : "mp3",
      },
    });

    const currentRequest = ++requestCounter.current;

    // Removed Bytescale authentication for now
    // while (!bytescale.AuthManager.isAuthSessionReady()) {
    //   await new Promise((resolve) => setTimeout(resolve, 100));
    // }

    const response = await fetchUrlMetadata(src);

    if (currentRequest !== requestCounter.current) {
      // Another play request has been made since this one started, so we ignore this one.
      return;
    }

    if ("error" in response) {
      onError?.(response.error.message, "fetch");
      setLoading(false);
      return;
    }

    if (!response.summary) return;

    function handleError(error: unknown, type: PlayErrorType) {
      onError?.(
        typeof error === "string" ? error : JSON.stringify(error),
        type,
      );
      setLoading(false);
      setPlaying(false);
    }

    const newAudio = new Howl({
      src: [response.summary.result.artifactUrl],
      onloaderror: (_soundId, error) => handleError(error, "load"),
      onplayerror: (_soundId, error) => handleError(error, "play"),
      onend: () => setPlaying(false),
      onstop: () => setPlaying(false),
      onload: () => setLoading(false),
    });

    setCurrentAudio(newAudio);
    newAudio.play();
    setPlaying(true);
  };

  return {
    pause,
    play,
    playing,
    playedSource,
    loading,
  };
}
